// Import Packages
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useRecoilValue } from 'recoil';

// Import Material UI
import StorageIcon from '@material-ui/icons/Storage';
import StoreIcon from '@material-ui/icons/Store';
import GetAppIcon from '@material-ui/icons/GetApp';
import FolderSpecialIcon from '@material-ui/icons/FolderSpecial';
import Chip from "@material-ui/core/Chip";
import { Grid, Button, Tab, Tooltip } from "@material-ui/core";
import Brightness1Icon from "@material-ui/icons/Brightness1";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";

// Import Pages
import LibraryService from "../../../services/LibraryService/LibraryService";
import CustomizeView from "../../../components/DataTable/CustomizeView/CustomizeView";
import Filters from "../../../components/DataTable/Filters/Filters";
import NewLibrary from "./NewLibrary/NewLibrary";
import { personalizationState, licensePermission } from "../../../recoil-store/recoil-state";
import { convertToDateFormat } from '../../../helpers/helper';
import { StyledTabs } from "../../../components/FlowConfigurationInputs";
import DataTable from "../../../components/DataTable/DataTable";
import Search from "./../../../components/Search/Search";

// Import CSS
import "./Libraries.css";

// Import Media Tools
import AddOnIcon from "../../../assets/icons/add-on.png";

const defaultLibraryImg = `default_library.png`;

function Libraries(props) {
    const { t } = useTranslation();
    const label = 'CONTENT.LIBRARIES.';
    const license = useRecoilValue(licensePermission);
    const personalization = useRecoilValue(personalizationState);
    const [customLibraryDevelopment, setCustomLibraryDevelopment] = useState(false);
    const [searchData, setSearchData] = useState([]);
    const [customColumn, setCustomColumn] = useState([]);
    const [openCustomizeView, setOpenCustomizeView] = useState(false);
    const [openFilters, setOpenFilters] = useState(false);
    const [open, setOpen] = useState(false);
    const [data, setData] = useState([]);
    const [filter, setFilter] = useState({ queryParam: { is: 1, environment_target: 'local' } });
    const [loading, setLoading] = useState(false);
    const [refresh, setRefresh] = useState(false);
    const [tab, setTab] = useState(0);
    const [columnList, setColumnList] = useState([]);

    async function fetchData(val) {
        setLoading(true);

        const response = await LibraryService.getLibrary(filter);
        if (response) {
            if (response?.status) {
                setData(response.data);
                setSearchData(response.data);
            }
            setLoading(false);
        }
    }

    function compare(a, b) {
        let versionNumber = Number(a.version.replace(".", ""));
        let versionNumberSecond = Number(b.version.replace(".", ""));
        if (versionNumber < versionNumberSecond) {
            return -1;
        }
        if (versionNumber > versionNumberSecond) {
            return 1;
        }
        return 0;
    }

    function handleOpen(open, setState) {
        setState(!open)
    }

    const handleChange = (event, newValue) => {
        let queryParam = {};
        switch (newValue) {
            case 0:
                queryParam = { is: 1, environment_target: 'local' };
                setFilter({ ...filter, queryParam })
                setColumnList(["id", "status", "libraryName", "vendor", "activeVersion", "lastVersion", "modifiedDate", "actions"]);
                break;
            // case 1:
            //     queryParam = {is: 0, i: 0, environment_target: 'global'};
            //     setFilter({...filter, queryParam})
            //     setColumnList(["id", "status", "libraryName", "vendor", "lastVersion", "modifiedDate", "actions"]);
            //     break;
            // case 2:
            //     queryParam = {is: 0, i: 1, environment_target: 'local'};
            //     setFilter({...filter, queryParam})
            //     setColumnList(["id", "status", "libraryName", "vendor", "activeVersion", "lastVersion", "modifiedDate", "actions"]);
            //     break;
            case 1:
                queryParam = { is: 0, m: 1, environment_target: 'local' };
                setFilter({ ...filter, queryParam })
                setColumnList(["id", "status", "libraryName", "vendor", "devVersion", "activeVersion", "lastVersion", "modifiedDate", "actions"]);
                break;
            default:
                break;
        }

        setSearchData([]);
        setTab(newValue);
    };

    const organizeDataTable = function () {
        let column = []
        const id = {
            key: "id", value: {
                id: 0,
                label: " ",
                name: "ids",
                options: {
                    filter: false,
                    sort: false,
                    display: true,
                },
            }
        };
        const status = {
            key: "status", value: {
                id: 1,
                label: " ",
                name: "status",
                options: {
                    filter: false,
                    sort: false,
                    customBodyRenderLite: (dataIndex, rowIndex) => {
                        if (searchData[dataIndex]) {
                            let activeItem = filter.queryParam.environment_target === 'local' ? searchData[dataIndex].library_versions?.find(item => item.is_active) : false;
                            if (activeItem) {
                                return (
                                    <div key={0}>
                                        <Brightness1Icon
                                            style={{
                                                width: 13,
                                                height: 13,
                                                color: "#43AA41",
                                            }}
                                        />
                                    </div>
                                )
                            } else {
                                return (
                                    <div key={0}>
                                        <Brightness1Icon
                                            style={{
                                                width: 13,
                                                height: 13,
                                                color: "#D45D5E",
                                            }}
                                        />
                                    </div>
                                )
                            }
                        }
                    },
                },
            }
        };
        const libraryName = {
            key: "libraryName", value: {
                id: 2,
                name: "library_name",
                label: t("CONTENT.LIBRARIES.LIBRARY-NAME"),
                options: {
                    filter: true,
                    sort: true,
                    customBodyRenderLite: (dataIndex) => {
                        const is_local = filter.queryParam.environment_target === 'local';
                        if (searchData[dataIndex]) {
                            const image = searchData[dataIndex]?.image?.includes('data:') ? searchData[dataIndex].image :
                                require(`../../../../public/images/libraries/${(searchData[dataIndex].is_system ? "system" : "custom")}/${searchData[dataIndex].image ? searchData[dataIndex].image : defaultLibraryImg}`)
                            return (
                                <div style={{ marginRight: 5 }}>
                                    {
                                        is_local ? (
                                            <div className="libraries-row">
                                                <img alt="library_icon" src={image} width="18px"
                                                    height="18px" />
                                                {searchData[dataIndex]?.library_label.includes('CUSTOM') ? searchData[dataIndex]?.library_name :
                                                    t(
                                                        "CONTENT.LIBRARY." +
                                                        searchData[dataIndex]?.library_name +
                                                        ".NAME"
                                                    )}
                                            </div>
                                        ) : (
                                            <div className="libraries-row">
                                                <img alt="library_icon"
                                                    src={image}
                                                    width="18px" height="18px" />
                                                {searchData[dataIndex]?.name}
                                            </div>
                                        )
                                    }

                                </div>
                            );
                        }
                    },
                },
            }
        };
        const vendor = {
            key: "vendor", value: {
                id: 3,
                name: "vendor",
                label: t("CONTENT.LIBRARIES.VENDOR"),
                options: {
                    filter: true,
                    sort: false,
                    customBodyRenderLite: (dataIndex) => {
                        if (searchData[dataIndex]) {
                            return (
                                <div>
                                    <div
                                        key={0}>{searchData[dataIndex].vendor ? searchData[dataIndex].vendor : '-'}</div>
                                </div>
                            );
                        }
                    },
                },
            }
        };
        const devVersion = {
            key: "devVersion", value: {
                id: 4,
                name: "library_versions",
                label: t("CONTENT.LIBRARIES.DEVELOPMENT-VERSION"),
                options: {
                    filter: true,
                    sort: true,
                    display: true,
                    customBodyRenderLite: (dataIndex) => {
                        if (searchData[dataIndex]) {
                            let activeItem = searchData[dataIndex].library_versions?.find(item => !item.is_published && !item.is_active);
                            if (activeItem) {
                                return (
                                    <div>
                                        <Chip style={{ marginRight: "5px" }} size="small"
                                            label={`v${activeItem.version}`} />
                                        <Chip size="small"
                                            label={`${activeItem.actions?.length} ${t(label + 'HAS-ACTIONS')}`} />
                                    </div>
                                )
                            } else {
                                return (
                                    <div key={0}>-</div>
                                )
                            }
                        }
                    },
                },
            }
        };
        const activeVersion = {
            key: "activeVersion", value: {
                id: 5,
                name: "version",
                label: t("CONTENT.LIBRARIES.VERSION"),
                options: {
                    filter: true,
                    sort: true,
                    display: true,
                    customBodyRenderLite: (dataIndex) => {
                        if (searchData[dataIndex]) {
                            let activeItem = searchData[dataIndex].library_versions?.find(item => item.is_active);
                            if (activeItem) {
                                return (
                                    <div>
                                        <Chip style={{ marginRight: "5px" }} color="primary" size="small"
                                            label={`v${activeItem.version}`} />
                                        <Chip size="small"
                                            label={`${activeItem.actions?.length} ${t(label + 'HAS-ACTIONS')}`} />
                                    </div>
                                )
                            } else {
                                return (
                                    <div key={0}>{t(label + "NO-ACTIVE-EXISTS")}</div>
                                )
                            }
                        }
                    },
                },
            }
        };
        const newVersion = {
            key: "lastVersion", value: {
                id: 6,
                name: "library_versions",
                label: t("CONTENT.LIBRARIES.LAST-VERSION"),
                options: {
                    filter: true,
                    sort: true,
                    display: true,
                    customBodyRenderLite: (dataIndex) => {
                        const is_local = filter.queryParam.environment_target === 'local';
                        if (searchData[dataIndex]) {
                            if (is_local && !searchData[dataIndex].last_version) {
                                let activeItem = searchData[dataIndex].library_versions?.filter(item => item.is_published);
                                if (activeItem.length) {
                                    return (
                                        <div>
                                            <Chip style={{ marginRight: "5px" }} color="secondary" size="small"
                                                label={`v${activeItem[activeItem.length - 1].version}`} />
                                            <Chip size="small"
                                                label={`${activeItem[activeItem.length - 1].actions?.length} ${t(label + 'HAS-ACTIONS')}`} />
                                        </div>
                                    )
                                } else {
                                    return (
                                        <div key={0}>{t(label + "NOT-PUBLISHED-YET")}</div>
                                    )
                                }
                            } else {
                                return (
                                    <div>
                                        <Chip style={{ marginRight: "5px" }} color="secondary" size="small"
                                            label={`v${searchData[dataIndex].last_version}`} />
                                        <Chip size="small"
                                            label={`${searchData[dataIndex].action_count} ${t(label + 'HAS-ACTIONS')}`} />
                                    </div>
                                )
                            }
                        }
                        // if (searchData[dataIndex]) {
                        //     let newVer =
                        //         searchData[dataIndex].library_versions &&
                        //         searchData[dataIndex]?.library_versions.sort(compare);
                        //     newVer && newVer.reverse();
                        //     return newVer && <Chip color="primary" size="small" label={newVer[0].version}/>;
                        // }
                    },
                },
            }
        };
        const modifiedDate = {
            key: "modifiedDate", value: {
                id: 7,
                name: "modified",
                label: t("CONTENT.LIBRARIES.MODIFIED-DATE"),
                options: {
                    filter: true,
                    sort: true,
                    customBodyRenderLite: (dataIndex) => {
                        if (searchData[dataIndex]) {
                            return (
                                <div
                                    key={0}>{convertToDateFormat(searchData[dataIndex].updated_at, personalization.date_format)}</div>
                            )
                        }
                    },
                },
            }
        };
        const actions = {
            key: "actions", value: {
                id: 8,
                name: "actions",
                label: " ",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRenderLite(dataIndex) {
                        const is_local = filter.queryParam.environment_target === 'local';
                        return (
                            <Link
                                data-test="autom-link"
                                to={`/my-environment/libraries/${is_local ? searchData[dataIndex]?.id : searchData[dataIndex]?.library_id}?t=${filter?.queryParam?.environment_target}`}
                            >
                                <ArrowForwardIcon className="arrow-forward-icon" />
                            </Link>
                        );
                    },
                },
            }
        };
        let columns = [id, status, libraryName, vendor, devVersion, activeVersion, newVersion, modifiedDate, actions];

        columnList.map((item, index) => {
            const matchedColumn = columns.find(colItem => colItem.key === item);
            const matchedValue = matchedColumn.value;
            matchedValue.id = index;
            column.push(matchedValue);
        });

        setCustomColumn(column);
    }

    useEffect(() => {
        setColumnList(["id", "status", "libraryName", "vendor", "activeVersion", "lastVersion", "modifiedDate", "actions"]);
        if (license.data.add_ons && license.data.add_ons.find(x => x === 'CustomLibraryDevelopment')) {
            setCustomLibraryDevelopment(true);
        }
    }, []);

    useEffect(() => {
        fetchData(filter).then(r => r);
    }, [refresh, filter]);

    useEffect(() => {
        organizeDataTable();
    }, [searchData, setCustomColumn, t, data]);

    return (
        <div style={{ width: "100%" }}>
            <Search
                xs={11} sm={11} md={11} lg={4} xl={4} spacing={2}
                data={data}
                searchData={searchData}
                setSearchData={setSearchData}
                searchKey={["library_name"]}
            >
                <Grid item xs={12} sm={12} md={8} lg={8} xl={8}>
                    {customLibraryDevelopment && (
                        <Button className="searchbar-button"
                            onClick={() => setOpen(true)}>{t(label + 'ADD-NEW')}
                            <Tooltip title={t("GENERAL.ADD-ON")} placement="top">
                                <img className="add-on-feature-button" src={AddOnIcon} alt="add-on feature" />
                            </Tooltip>
                        </Button>
                    )}
                    <Button className="searchbar-button"
                        onClick={() => handleOpen(openCustomizeView, setOpenCustomizeView)}>{t('CONTENT.DATATABLE.TOOLBAR.CUSTOMIZE-VIEW')}</Button>
                    <Button className="searchbar-button"
                        onClick={() => handleOpen(openFilters, setOpenFilters)}>{t('CONTENT.DATATABLE.TOOLBAR.FILTERS')}</Button>
                </Grid>
            </Search>
            <div>
                <StyledTabs value={tab} onChange={handleChange}>
                    <Tab label={t(label + 'SYSTEM')} value={0} icon={<StorageIcon />} style={{ minWidth: "140px" }} />
                    {(window.location.hostname.indexOf('136.244.100.164') === -1 && window.location.hostname.indexOf('global.autommate.com') === -1 && window.location.hostname.indexOf('global.autommate.app') === -1) &&
                        <Tab label={t(label + 'MY-CUSTOM-LIBRARIES')} value={1} icon={<FolderSpecialIcon />}
                            style={{ minWidth: "140px" }} />}
                    {/*<Tab label={t(label + 'VARIANTS')} value={2}/>*/}
                </StyledTabs>
            </div>

            <DataTable
                data={searchData}
                customColumns={customColumn}
                title={t("MESSAGES.MONITORING.AUTOM-MONITORING")}
                loading={loading}
            />
            {openCustomizeView && <CustomizeView setOpen={setOpenCustomizeView} customColumn={customColumn}
                setCustomColumn={setCustomColumn} />}
            {openFilters &&
                <Filters setOpen={setOpenFilters} customColumn={customColumn} setCustomColumn={setCustomColumn} />}
            {open && <NewLibrary open={open} setOpen={setOpen} setRefresh={setRefresh} />}
        </div>
    );
}

export default Libraries;
